import { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useAccount, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { parseUnits, maxUint256 } from 'viem';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { useBalance } from '../hooks/useBalance';
import { useTokenAllowance } from '../hooks/useTokenAllowance';
import { useUniversalSwapQuote } from '../hooks/useUniversalSwapQuote';
import { useTokenUsdPrice } from '../hooks/useTokenUsdPrice';
import { formatNumber, formatUsd, normalizeDecimalInput } from '../utils/format';
import { formatTokenAmount, formatBalance } from '../utils/tokenValueDetector';
import { formatErrorForToast } from '../utils/errorMessages';
import { useToast } from '../contexts/ToastContext';
import Confetti from '../components/Confetti/Confetti';
import { SkeletonBox } from '../components/Skeleton/Skeleton';
import Tooltip from '../components/Tooltip/Tooltip';
import { TOKEN_LIST } from '../config/tokens';
import {
  PANCAKE_ROUTER_V2,
  ERC20_ABI,
  PANCAKE_ROUTER_V2_ABI,
  WBNB_ADDRESS
} from '../config/contracts';
import TokenModal from '../components/TokenModal';
import SettingsModal from '../components/SettingsModal';
import ConfirmSwapModal from '../components/ConfirmSwapModal';
import WalletModal from '../components/WalletModal';
import SwapHeader from '../components/Swap/SwapHeader';
import SwapInputPanel from '../components/Swap/SwapInputPanel';
import SwapFlipButton from '../components/Swap/SwapFlipButton';
import '../App.css';
import '../SwapModern.css';

const Swap = () => {
  const { address, isConnected } = useAccount();
  const { open } = useWeb3Modal();
  const { toast } = useToast();

  const [fromToken, setFromToken] = useState(TOKEN_LIST[0]); // BNB
  const [toToken, setToToken] = useState(TOKEN_LIST[1]); // LRST
  const [fromAmount, setFromAmount] = useState('');
  const [slippageBps, setSlippageBps] = useState(50);
  const [slippageMode, setSlippageMode] = useState('auto');
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenModalRole, setTokenModalRole] = useState('from');
  const [showSettings, setShowSettings] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [swapSuccessShown, setSwapSuccessShown] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const fromBalance = useBalance(address, fromToken);
  const toBalance = useBalance(address, toToken);
  
  // Debug: Log balance whenever it changes
  useEffect(() => {
    if (fromToken && fromBalance) {
      console.log('[Swap] fromBalance updated:', {
        token: fromToken.symbol,
        balance: fromBalance.balance,
        formatted: fromBalance.formatted,
        type: typeof fromBalance.balance
      });
    }
  }, [fromBalance.balance, fromToken]);

  const { usdPrice: fromUsdPrice } = useTokenUsdPrice(fromToken);
  const { usdPrice: toUsdPrice } = useTokenUsdPrice(toToken);

  const { quote, loading: quoteLoading, routerUsed } = useUniversalSwapQuote(
    fromToken,
    toToken,
    parseFloat(fromAmount || '0'),
    slippageBps
  );

  // Smart formatting: auto-detect high-value vs low-value tokens (AFTER quote is defined)
  const formattedToAmount = useMemo(() => {
    if (!quote) return '';
    return formatTokenAmount(quote.amountOut, toToken, toUsdPrice, null, 6);
  }, [quote, toToken, toUsdPrice]);

  const formattedMinReceived = useMemo(() => {
    if (!quote) return '';
    const minOut = Number(quote.minOut) / 10 ** toToken.decimals;
    return formatTokenAmount(minOut, toToken, toUsdPrice, null, 6);
  }, [quote, toToken, toUsdPrice]);

  const formattedFromBalance = useMemo(() => {
    if (!fromBalance.balance) return '0';
    return formatBalance(fromBalance.balance, fromToken, fromUsdPrice);
  }, [fromBalance.balance, fromToken, fromUsdPrice]);

  const formattedToBalance = useMemo(() => {
    if (!toBalance.balance) return '0';
    return formatBalance(toBalance.balance, toToken, toUsdPrice);
  }, [toBalance.balance, toToken, toUsdPrice]);

  const { allowance, hasAllowance, refetch: refetchAllowance } = useTokenAllowance(
    fromToken?.isNative ? null : fromToken?.address,
    address,
    PANCAKE_ROUTER_V2
  );

  const {
    writeContract: writeApprove,
    data: approveHash,
    isPending: isApproving,
    error: approveWriteError
  } = useWriteContract();

  const { 
    isSuccess: isApproveSuccess,
    isError: isApproveError,
    error: approveConfirmError
  } = useWaitForTransactionReceipt({
    hash: approveHash
  });

  const {
    writeContract: writeSwap,
    data: swapHash,
    isPending: isSwapping,
    error: swapWriteError,
    reset: resetSwap
  } = useWriteContract();

  const { 
    isSuccess: isSwapSuccess,
    isError: isSwapError,
    error: swapConfirmError
  } = useWaitForTransactionReceipt({
    hash: swapHash
  });

  useEffect(() => {
    if (isApproveSuccess) {
      toast.success('Token Approved!', 'You can now proceed with the swap');
      refetchAllowance();
    }
  }, [isApproveSuccess, refetchAllowance]);

  useEffect(() => {
    if (approveWriteError) {
      const { title, message } = formatErrorForToast(approveWriteError);
      toast.error(title, message);
    }
  }, [approveWriteError]);

  useEffect(() => {
    if (isApproveError && approveConfirmError) {
      const { title, message } = formatErrorForToast(approveConfirmError);
      toast.error(title, message);
    }
  }, [isApproveError, approveConfirmError]);

  useEffect(() => {
    if (isSwapSuccess && swapHash && !swapSuccessShown) {
      const toAmount = quote ? formatTokenAmount(quote.amountOut, toToken, null, null, 2) : '';
      toast.success(
        'Swap Successful!', 
        `You swapped ${fromAmount} ${fromToken.symbol} for ${toAmount} ${toToken.symbol}`
      );
      setShowConfetti(true);
      setSwapSuccessShown(true);
      setFromAmount('');
      setShowConfirm(false);
      
      setTimeout(() => {
        resetSwap();
        setSwapSuccessShown(false);
      }, 2000);
      
      setTimeout(() => {
        fromBalance.refetch?.();
        toBalance.refetch?.();
      }, 3000);
    }
  }, [isSwapSuccess, swapHash, swapSuccessShown, resetSwap, fromBalance, toBalance, quote, fromAmount, fromToken, toToken]);

  useEffect(() => {
    if (swapWriteError) {
      const { title, message } = formatErrorForToast(swapWriteError);
      toast.error(title, message);
    }
  }, [swapWriteError]);

  useEffect(() => {
    if (isSwapError && swapConfirmError) {
      const { title, message } = formatErrorForToast(swapConfirmError);
      toast.error(title, message);
    }
  }, [isSwapError, swapConfirmError]);

  const handleFromAmountChange = (e) => {
    const val = normalizeDecimalInput(e.target.value);
    setFromAmount(val);
  };

  const handlePercentClick = (pct) => {
    console.log('[PercentClick] Start -', { pct, isConnected, fromToken: fromToken?.symbol });
    
    if (!isConnected) {
      console.log('[PercentClick] Not connected, opening wallet modal');
      open();
      return;
    }
    
    console.log('[PercentClick] Balance data:', {
      balance: fromBalance.balance,
      formatted: fromBalance.formatted,
      tokenSymbol: fromToken?.symbol,
      tokenDecimals: fromToken?.decimals
    });
    
    const bal = fromBalance.balance;
    
    if (!bal || bal === 0) {
      console.log('[PercentClick] Balance is 0 or undefined, cannot calculate percentage');
      return;
    }
    
    let amount = bal * (pct / 100);
    console.log('[PercentClick] Calculated amount:', amount);
    
    // High-value tokens (BNB, ETH, etc.) that should keep more decimal precision
    const HIGH_VALUE_TOKENS = ['BNB', 'WBNB', 'ETH', 'BTCB'];
    const isHighValueToken = HIGH_VALUE_TOKENS.includes(fromToken?.symbol?.toUpperCase());
    
    if (pct === 100) {
      // MAX: Reserve some for gas
      if (!fromToken?.isNative) {
        amount *= 0.99; // Reserve 1% for ERC20 tokens
      } else {
        // For native tokens, reserve small amount OR 1%, whichever is smaller
        const gasReserve = Math.min(0.01, amount * 0.05); // Reserve max 0.01 or 5%
        amount = Math.max(0, amount - gasReserve);
      }
      console.log('[PercentClick] MAX - Final amount:', amount);
      setFromAmount(amount > 0 ? String(amount) : '');
    } else {
      // Percentage buttons (10%, 25%, 50%)
      // DON'T subtract gas for percentage buttons - use exact percentage
      // User can manually adjust if needed
      
      if (isHighValueToken) {
        // High-value tokens: Keep decimals for precision
        let formattedAmount;
        if (amount < 1) {
          formattedAmount = amount.toFixed(8).replace(/\.?0+$/, '');
        } else {
          formattedAmount = amount.toFixed(6).replace(/\.?0+$/, '');
        }
        console.log('[PercentClick] High-value token - Formatted:', formattedAmount);
        setFromAmount(amount > 0 ? formattedAmount : '');
      } else {
        // Low-value tokens: Keep full number without rounding
        console.log('[PercentClick] Low-value token - Keep full:', amount);
        setFromAmount(amount > 0 ? String(amount) : '');
      }
    }
  };

  const handleFlip = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setFromAmount('');
  };

  const openTokenSelector = (role) => {
    setTokenModalRole(role);
    setShowTokenModal(true);
  };

  const handleTokenSelect = (token) => {
    if (tokenModalRole === 'from') {
      if (token.address.toLowerCase() === toToken.address.toLowerCase()) {
        setToToken(fromToken);
      }
      setFromToken(token);
    } else {
      if (token.address.toLowerCase() === fromToken.address.toLowerCase()) {
        setFromToken(toToken);
      }
      setToToken(token);
    }
    setShowTokenModal(false);
  };

  const handleApprove = async () => {
    if (!fromToken || fromToken.isNative) return;
    try {
      writeApprove({
        address: fromToken.address,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [PANCAKE_ROUTER_V2, maxUint256]
      });
      toast.info('Approval pending...');
    } catch (err) {
      toast.error('Approval failed');
      console.error(err);
    }
  };

  const handleSwap = async () => {
    if (!quote || !address) return;
    setShowConfirm(false);

    const loadingToastId = toast.loading('Processing swap transaction...');

    try {
      const deadline = Math.floor(Date.now() / 1000) + 600;
      const amountIn = parseUnits(fromAmount, fromToken.decimals);

      if (fromToken.isNative) {
        writeSwap({
          address: PANCAKE_ROUTER_V2,
          abi: PANCAKE_ROUTER_V2_ABI,
          functionName: 'swapExactETHForTokensSupportingFeeOnTransferTokens',
          args: [quote.minOut, quote.path, address, deadline],
          value: amountIn
        });
      } else if (toToken.isNative) {
        writeSwap({
          address: PANCAKE_ROUTER_V2,
          abi: PANCAKE_ROUTER_V2_ABI,
          functionName: 'swapExactTokensForETHSupportingFeeOnTransferTokens',
          args: [amountIn, quote.minOut, quote.path, address, deadline]
        });
      } else {
        writeSwap({
          address: PANCAKE_ROUTER_V2,
          abi: PANCAKE_ROUTER_V2_ABI,
          functionName: 'swapExactTokensForTokensSupportingFeeOnTransferTokens',
          args: [amountIn, quote.minOut, quote.path, address, deadline]
        });
      }

      toast.dismiss(loadingToastId);
    } catch (err) {
      toast.dismiss(loadingToastId);
      const { title, message } = formatErrorForToast(err);
      toast.error(title, message);
      console.error(err);
    }
  };

  const buttonState = useMemo(() => {
    if (!isConnected) return { text: 'Enter Amount', action: null, disabled: true };
    if (!fromAmount || parseFloat(fromAmount) <= 0)
      return { text: 'Enter amount', action: null, disabled: true };
    if (quoteLoading) return { text: 'Fetching quote...', action: null, disabled: true };
    if (!quote) return { text: 'No route found', action: null, disabled: true };
    if (isSwapping) return { text: 'Swapping...', action: null, disabled: true };
    if (isApproving) return { text: 'Approving...', action: null, disabled: true };
    if (fromToken.isNative)
      return { text: 'Swap', action: () => setShowConfirm(true), disabled: isSwapping || isApproving };

    const amountIn = parseUnits(fromAmount, fromToken.decimals);
    if (!hasAllowance(amountIn))
      return { text: `Approve ${fromToken.symbol}`, action: handleApprove, disabled: isApproving || isSwapping };

    return { text: 'Swap', action: () => setShowConfirm(true), disabled: isSwapping || isApproving };
  }, [isConnected, fromAmount, quoteLoading, quote, isSwapping, isApproving, fromToken, hasAllowance, handleApprove]);

  // Professional animations
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.8 } },
    exit: { opacity: 0, transition: { duration: 0.3 } }
  };

  const cardVariants = {
    initial: { opacity: 0, y: 30, scale: 0.97 },
    animate: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <>
      <Helmet>
        <title>Little Rabbit Swap</title>
        <link rel="icon" type="image/png" href="/favicon-round.png?v=4" />
        <link rel="shortcut icon" href="/favicon-round.png?v=4" />
      </Helmet>
      <motion.div 
        className="page-container-narrow"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
      <div className="lr-main">
        <div className="lr-container lr-main-grid">
          <motion.section
            className="lr-column-left"
            variants={cardVariants}
            initial="initial"
            animate="animate"
          >
            <div className="lr-card lr-card-swap lr-card-swap-v2">
              <SwapHeader 
                routerUsed={routerUsed} 
                onSettingsClick={() => setShowSettings(true)} 
              />

              <div className="lr-card-body-modern">
                <SwapInputPanel
                  label="You Pay"
                  token={fromToken}
                  balance={formattedFromBalance}
                  amount={fromAmount}
                  usdValue={fromAmount && fromUsdPrice ? parseFloat(fromAmount) * fromUsdPrice : null}
                  onAmountChange={handleFromAmountChange}
                  onTokenSelect={() => openTokenSelector('from')}
                  onPercentClick={handlePercentClick}
                  showQuickAmounts={true}
                  isConnected={isConnected}
                />

                <SwapFlipButton onFlip={handleFlip} />

                <SwapInputPanel
                  label="You Receive"
                  token={toToken}
                  balance={formattedToBalance}
                  amount={formattedToAmount}
                  usdValue={quote && toUsdPrice ? quote.amountOut * toUsdPrice : null}
                  onTokenSelect={() => openTokenSelector('to')}
                  readOnly={true}
                />
              </div>

              <div>
                <motion.button
                  className={`lr-btn lr-btn-primary lr-btn-block ${
                    (buttonState.disabled || isSwapping || isApproving) ? 'lr-btn-pending' : ''
                  } ${!isConnected ? 'not-connected' : ''}`}
                  onClick={!isSwapping && !isApproving ? buttonState.action : undefined}
                  disabled={buttonState.disabled || isSwapping || isApproving}
                  style={{
                    cursor: (buttonState.disabled || isSwapping || isApproving) ? 'not-allowed' : 'pointer',
                    opacity: (isSwapping || isApproving) ? 0.6 : 1
                  }}
                  data-press
                  whileHover={!buttonState.disabled && !isSwapping && !isApproving ? { 
                    scale: 1.015, 
                    y: -2,
                    transition: { duration: 0.2, ease: "easeOut" }
                  } : {}}
                  whileTap={!buttonState.disabled && !isSwapping && !isApproving ? { 
                    scale: 0.98,
                    transition: { duration: 0.1 }
                  } : {}}
                >
                  {isSwapping ? (
                    <>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        Swapping...
                        <span className="spinner-dots">
                          <span>.</span><span>.</span><span>.</span>
                        </span>
                      </span>
                    </>
                  ) : isApproving ? (
                    <>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        Approving...
                        <span className="spinner-dots">
                          <span>.</span><span>.</span><span>.</span>
                        </span>
                      </span>
                    </>
                  ) : buttonState.text}
                </motion.button>

                {quoteLoading && fromAmount && parseFloat(fromAmount) > 0 && (
                  <motion.div
                    className="lr-swap-meta"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                    style={{ padding: '12px' }}
                  >
                    <SkeletonBox width="100%" height="16px" style={{ marginBottom: '8px' }} />
                    <SkeletonBox width="100%" height="16px" style={{ marginBottom: '8px' }} />
                    <SkeletonBox width="80%" height="16px" />
                  </motion.div>
                )}

                {quote && !quoteLoading && (
                  <motion.div
                    className="lr-swap-meta"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="lr-swap-meta-row">
                      <span className="lr-meta-label">Price</span>
                      <span className="lr-meta-value">
                        1 {fromToken.symbol} ≈ {formatTokenAmount(
                          quote.amountOut / parseFloat(fromAmount || 1), 
                          toToken, 
                          toUsdPrice, 
                          null, 
                          0
                        )} {toToken.symbol}
                      </span>
                    </div>
                    <div className="lr-swap-meta-row">
                      <span className="lr-meta-label">Route</span>
                      <span className="lr-meta-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>{fromToken.symbol}</span>
                        <span style={{ fontSize: '12px', opacity: 0.7 }}>→</span>
                        <span>{toToken.symbol}</span>
                      </span>
                    </div>
                    <div className="lr-swap-meta-row">
                      <Tooltip 
                        content="Maximum price change you'll accept. If price moves more than this during the transaction, it will fail to protect you." 
                        position="top"
                      >
                        <span className="lr-meta-label" style={{ cursor: 'help', borderBottom: '1px dotted rgba(255,255,255,0.3)' }}>
                          Slippage
                        </span>
                      </Tooltip>
                      <span className="lr-meta-value">
                        {slippageMode === 'auto' ? 'Auto' : `${(slippageBps / 100).toFixed(2)}%`}
                      </span>
                    </div>
                    <div className="lr-swap-meta-row">
                      <span className="lr-meta-label">Minimum received</span>
                      <span className="lr-meta-value">
                        {formattedMinReceived}{' '}
                        {toToken.symbol}
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.section>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {showTokenModal && (
          <TokenModal
            isOpen={showTokenModal}
            onClose={() => setShowTokenModal(false)}
            onSelect={handleTokenSelect}
            selectedToken={tokenModalRole === 'from' ? fromToken : toToken}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {showSettings && (
          <SettingsModal
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            slippageBps={slippageBps}
            slippageMode={slippageMode}
            onSlippageChange={(bps, mode) => {
              setSlippageBps(bps);
              setSlippageMode(mode);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {showConfirm && (
          <ConfirmSwapModal
            isOpen={showConfirm}
            onClose={() => setShowConfirm(false)}
            onConfirm={handleSwap}
            fromToken={fromToken}
            toToken={toToken}
            fromAmount={fromAmount}
            toAmount={formattedToAmount}
            slippageBps={slippageBps}
            slippageMode={slippageMode}
            minReceived={formattedMinReceived}
          />
        )}
      </AnimatePresence>

      <WalletModal
        isOpen={showWalletModal}
        onClose={() => setShowWalletModal(false)}
      />

      <Confetti 
        active={showConfetti}
        duration={3000}
        onComplete={() => setShowConfetti(false)}
      />
    </motion.div>
    </>
  );
};

export default Swap;
